<template>
    <div>
        <base-dialog :loading="loading" :value="dialog" :width="1040" :fullscreen="isMobile" :title="$t('common.evaluation_report')"
            @input="onInput" @close="hide">
            <skeleton-loader v-if="loading" type="rows" />

            <div v-else-if="!student || !hasData" class="report-empty">
                <v-icon size="40" color="txtMuted">mdi-database-off-outline</v-icon>
                <p>{{ $t('common.no_values_student') }}</p>
            </div>

            <template v-else>
                <div class="report-subtitle">
                    <div class="report-subtitle-main">
                        <span class="report-student">{{ student.name }}</span>
                        <span class="report-date">· {{ $moment().format('DD/MM/YYYY') }}</span>
                    </div>
                    <img v-if="logoUrl" :src="logoUrl" class="report-logo" alt="" />
                </div>

                <div class="report-summary">
                    <div v-for="item in items" :key="item.id" class="summary-card">
                        <div class="summary-value" :class="{ 'sc-good': item.color === 'success' }">{{ item.actual }}
                        </div>
                        <div class="summary-label">{{ item.name }}</div>
                        <div class="summary-sub">{{ item.unit }}</div>
                    </div>
                </div>

                <div class="report-grid">
                    <div class="report-col">
                        <div class="report-section">
                            <div class="report-section-title">{{ $t('common.profile_evaluations') }}</div>
                            <radar-chart :labels="evaluationsName" :datasets="evaluationData" style="height: 230px;" />
                            <div class="chart-legend-row">
                                <div class="chart-legend-item">
                                    <span class="chart-legend-dot dot-primary" />
                                    {{ $t('common.actual') }}
                                </div>
                                <div class="chart-legend-item">
                                    <span class="chart-legend-dot dot-accent" />
                                    {{ $t('common.ideal') }}
                                </div>
                            </div>
                        </div>

                        <div class="report-field">
                            <label class="report-field-label good">{{ $t('common.your_strong_points') }}</label>
                            <textarea class="report-textarea" :placeholder="$t('common.load_comment')"
                                v-model="strongPoints" />
                        </div>

                        <div class="report-field">
                            <label class="report-field-label bad">{{ $t('common.improved_points') }}</label>
                            <textarea class="report-textarea" :placeholder="$t('common.load_comment')"
                                v-model="improvingPoints" />
                        </div>
                    </div>

                    <div class="report-col">
                        <div class="report-section report-bars-section">
                            <div class="report-section-title">{{ $t('common.summary_evaluations') }}</div>
                            <div class="report-bars">
                                <div v-for="item in items" :key="item.id" class="report-bar-row">
                                    <span class="report-bar-label">{{ item.name }}</span>
                                    <div class="report-bar-track">
                                        <div class="report-bar-fill" :style="barFillStyle(item)" />
                                        <div class="report-bar-ideal" :style="{ left: idealLeft(item) }" />
                                    </div>
                                    <span class="report-bar-value">{{ item.actual }} {{ item.unit }}</span>
                                </div>
                            </div>
                            <div class="chart-legend-row report-bars-legend">
                                <div class="chart-legend-item">
                                    <span class="chart-legend-dot dot-primary" />
                                    {{ $t('common.actual') }}
                                </div>
                                <div class="chart-legend-item">
                                    <span class="report-ideal-mark" />
                                    {{ $t('common.ideal') }}
                                </div>
                            </div>
                        </div>

                        <div class="report-field">
                            <label class="report-field-label">{{ $t('common.coach_comment') }}</label>
                            <textarea class="report-textarea" :placeholder="$t('common.load_comment')"
                                v-model="comment" />
                        </div>
                    </div>
                </div>
            </template>

            <template #footer>
                <dialog-actions :confirm-label="$t('common.save')" :disabled="!hasData" :loading="loadingSave"
                    @cancel="hide" @confirm="save" />
            </template>
        </base-dialog>
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { db, Timestamp } from "@/utils/Firebase";
import { sendMessage } from "@/utils/Messages";
import RadarChart from './RadarChart.vue';
import { getCurrentValuesArrayForEvaluation, getDatasetsForEvaluation, getItemsForEvaluation, getValuesForEvaluation } from '../../utils/Evaluations';
import BaseDialog from './BaseDialog';
import DialogActions from './DialogActions';
import SkeletonLoader from './SkeletonLoader.vue';

export default {
    name: "CreateReportStudent",
    components: { RadarChart, BaseDialog, DialogActions, SkeletonLoader },
    props: {
        coachId: {
            type: String,
            default: null
        },
    },
    data() {
        return {
            dialog: false,
            loading: false,
            loadingSave: false,
            student: null,
            strongPoints: '',
            improvingPoints: '',
            comment: '',
            assignedEvaluation: null,
        }
    },
    computed: {
        ...mapState(['evaluations']),
        logo(vm = this) {
            return vm.account?.logo?.url || vm.imgPlaceholder
        },
        logoUrl(vm = this) {
            return vm.account?.logo?.url || null
        },
        evaluationId(vm = this) {
            return vm.assignedEvaluation?.evaluationId || null
        },
        currentEvaluation(vm = this) {
            if (!vm.evaluationId) return null
            return vm.evaluations.find(eva => eva.id === vm.evaluationId) || null
        },
        config(vm = this) {
            return vm.currentEvaluation?.config || []
        },
        values(vm = this) {
            return getValuesForEvaluation(vm.assignedEvaluation?.values)
        },
        items(vm = this) {
            if (!vm.config || !vm.values) return []
            return getItemsForEvaluation(vm.config, vm.values)
        },
        hasData(vm = this) {
            return vm.items.length > 0
        },
        currentValues(vm = this) {
            if (!vm.values) return []
            return getCurrentValuesArrayForEvaluation(vm.values, vm.config, vm.items)
        },
        evaluationsName(vm = this) {
            return vm.items.map(item => item.name)
        },
        evaluationData(vm = this) {
            if (vm.items.length === 0) return [];
            return getDatasetsForEvaluation(vm.items)
        },
    },
    async mounted() {
        const vm = this
        vm.loadEvaluations()
    },
    methods: {
        ...mapActions(['bindEvaluations']),
        show(student = null) {
            const vm = this
            vm.setDefaults(student)
            vm.dialog = true
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.reset()
        },
        onInput(val) {
            if (!val) this.hide()
        },
        reset() {
            const vm = this
            vm.student = ''
            vm.strongPoints = ''
            vm.improvingPoints = ''
            vm.comment = ''
        },
        async loadEvaluations() {
            const vm = this;
            if (vm._checkNeedReloadBind(vm.evaluations, vm.coachId)) await vm.bindEvaluations(vm.coachId)
        },
        async setDefaults(student) {
            const vm = this;
            vm.student = student
            await vm.withLoading(async () => {
                let assignedRef = await db.collection('assignedEvaluations').doc(vm.student.assignedEvaluationId).get()
                vm.assignedEvaluation = { id: assignedRef.id, ...assignedRef.data() }
            })
        },
        barScale(item) {
            const nums = [item.actual, item.ideal, item.best, item.worst].map(Number).filter(n => !isNaN(n))
            if (!nums.length) return { lo: 0, span: 1 }
            let lo = Math.min(...nums), hi = Math.max(...nums)
            const pad = (hi - lo) * 0.12 || Math.abs(hi) * 0.12 || 1
            lo -= pad; hi += pad
            return { lo, span: (hi - lo) || 1 }
        },
        barFillStyle(item) {
            const { lo, span } = this.barScale(item)
            const w = Math.max(0, Math.min(100, ((Number(item.actual) - lo) / span) * 100))
            return { width: `${w}%` }
        },
        idealLeft(item) {
            const { lo, span } = this.barScale(item)
            const l = Math.max(0, Math.min(100, ((Number(item.ideal) - lo) / span) * 100))
            return `${l}%`
        },
        async save() {
            const vm = this
            await vm.withLoading(async () => {
                let payload = {
                    studentId: vm.student.id,
                    coachId: vm.coachId,
                    date: vm.$moment().format('YYYY-MM-DD'),
                    evaluationsName: vm.evaluationsName,
                    evaluationData: vm.evaluationData.map(data => {
                        delete data._meta
                        return data
                    }),
                    strongPoints: vm.strongPoints,
                    improvingPoints: vm.improvingPoints,
                    currentValues: vm.currentValues,
                    comment: vm.comment,
                    created_at: Timestamp.now(),
                    logo: vm.logo,
                    items: vm.items,
                    type: 'student'
                }

                await db.collection('evaluationReports').add(payload)

                await vm.sendMessageToStudent()
                vm.$emit('saved')
                vm.hide()
                vm.toast(vm.$t('common.report_saved'))
            }, 'loadingSave')
        },
        async sendMessageToStudent() {
            const vm = this

            let message = vm.$t('common.new_report_created')

            let coachId = vm.coachId
            let studentId = vm.student?.id

            if (!coachId || !studentId) return

            await sendMessage({ message, from: coachId }, coachId, studentId)
        },
    },
    watch: {
        coachId() {
            this.loadEvaluations()
        },
    }
}
</script>

<style scoped>
.report-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 48px 16px;
    color: var(--v-txtMuted-base);
    font-size: 14px;
    text-align: center;
}

.report-subtitle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 16px;
}

.report-subtitle-main {
    display: flex;
    align-items: baseline;
    gap: 6px;
    min-width: 0;
}

.report-logo {
    height: 40px;
    max-width: 140px;
    object-fit: contain;
    flex-shrink: 0;
}

.report-student {
    font-size: 16px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.report-date {
    font-size: 13px;
    color: var(--v-txtMuted-base);
}

.report-summary {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 10px;
    margin-bottom: 18px;
}

.summary-card {
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 12px;
    padding: 12px 10px;
    text-align: center;
}

.summary-value {
    font-size: 22px;
    font-weight: 800;
    color: var(--v-txtPrimary-base);
    line-height: 1.1;
}

.summary-value.sc-good {
    color: var(--v-primary-base);
}

.theme--light .summary-value.sc-good {
    color: var(--v-accent-base);
}

.summary-label {
    font-size: 12px;
    font-weight: 600;
    color: var(--v-txtSecondary-base);
    margin-top: 4px;
}

.summary-sub {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--v-txtMuted-base);
    margin-top: 2px;
}

.report-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
}

.report-col {
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-width: 0;
}

.report-section {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
    padding: 14px;
}

.report-bars-section {
    flex: 1;
}

.report-section-title {
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: var(--v-primary-base);
    margin-bottom: 12px;
}

.theme--light .report-section-title {
    color: var(--v-accent-base);
}

.report-bars {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.report-bar-row {
    display: grid;
    grid-template-columns: 96px 1fr 68px;
    align-items: center;
    gap: 10px;
}

.report-bar-label {
    font-size: 12px;
    color: var(--v-txtSecondary-base);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.report-bar-track {
    position: relative;
    height: 14px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 5px;
    overflow: hidden;
}

.report-bar-fill {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    background: rgba(204, 255, 51, 0.35);
    border-radius: 5px 0 0 5px;
}

.theme--light .report-bar-fill {
    background: rgba(51, 153, 153, 0.3);
}

.report-bar-ideal {
    position: absolute;
    top: -2px;
    width: 2px;
    height: 18px;
    background: var(--v-accent-base);
    border-radius: 1px;
}

.report-bar-value {
    font-size: 12px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
    text-align: right;
    white-space: nowrap;
}

.report-bars-legend {
    margin-top: 14px;
}

.report-ideal-mark {
    width: 2px;
    height: 12px;
    background: var(--v-accent-base);
    border-radius: 1px;
    flex-shrink: 0;
}

.report-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.report-field-label {
    font-size: 12px;
    font-weight: 700;
    color: var(--v-txtSecondary-base);
}

.report-field-label.good {
    color: var(--v-success-base);
}

.report-field-label.bad {
    color: var(--v-error-base);
}

.report-textarea {
    width: 100%;
    min-height: 72px;
    resize: vertical;
    padding: 10px 12px;
    border-radius: 10px;
    border: 1px solid var(--v-outline-base);
    background: var(--v-surface2-base);
    color: var(--v-txtPrimary-base);
    font-family: inherit;
    font-size: 13px;
    line-height: 1.45;
    outline: none;
    transition: border-color 0.15s;
}

.report-textarea:focus {
    border-color: var(--v-primary-base);
}

.theme--light .report-textarea:focus {
    border-color: var(--v-accent-base);
}

.report-textarea::placeholder {
    color: var(--v-txtMuted-base);
}

.chart-legend-row {
    display: flex;
    justify-content: center;
    gap: 16px;
    margin-top: 8px;
}

.chart-legend-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: var(--v-txtMuted-base);
}

.chart-legend-dot {
    width: 9px;
    height: 9px;
    border-radius: 2px;
    flex-shrink: 0;
}

.dot-primary {
    background: var(--v-primary-base);
}

.dot-accent {
    background: var(--v-accent-base);
}

@media (max-width: 820px) {
    .report-grid {
        grid-template-columns: 1fr;
    }
}
</style>
